import { useState, useEffect, useRef } from 'react';
import { C, FONT, TimeframeControlWithDates, useTimeframe } from './adminShared';
import { useAuth } from '../../hooks/useAuth';
import { getCallFunnel, setCallFunnel, setCallFunnelSessionLength, setCallFunnelPeriodTest, getCallFunnelStats } from '../../utils/api';

// ── ADS TO CALL TAB (temporary) ──────────────────────────────────────────────
// The Personal Blueprint Session call funnel: Ads → opt-in → masterclass →
// complimentary call. Build session 1: only the ON/OFF switch lives here. The funnel
// numbers (counted from the switch-on moment) come in a later session.
//
// THE SWITCH (backend config/callFunnel.js, ry_settings.call_funnel_periods)
// is the server's state, always re-read from the server:
//   • a failed read shows an error and NO button — the state is never guessed;
//   • the time of a flip is stamped by the server, not this browser;
//   • every flip is kept as a period in the history below.
// Owner only may flip it (the POST is ownerMiddleware-gated); a view-only admin
// sees the status and history with no button.

// Shown in the browser's own time zone (Ben's device), named so it can be
// checked. The stored value is UTC.
const fmtLocal = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString(undefined, {
    weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
    hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
  });
};

const CONFIRM_WORD = { on: 'SWITCH ON', off: 'SWITCH OFF' };

const CallFunnelSwitchCard = () => {
  const { user } = useAuth();
  const isOwner = user?.role === 'owner';

  const [status, setStatus] = useState(null);
  const [failed, setFailed] = useState(false);
  const [stage, setStage]   = useState('idle'); // 'idle' | 'confirm' | 'working'
  const [typed, setTyped]   = useState('');
  const [error, setError]   = useState('');
  // The session length (60 or 30 minutes): which Personal Blueprint Session the
  // pop-up books. Its own small confirm step; the server only accepts 60 or 30.
  const [lenStage, setLenStage] = useState('idle'); // 'idle' | 'confirm' | 'working'
  const [lenError, setLenError] = useState('');
  // Mark/unmark one history period as a practice run. testIdx is the period
  // index awaiting confirmation (null = none); testWorking guards the POST;
  // testError shows a failure beside that row.
  const [testIdx, setTestIdx] = useState(null);
  const [testWorking, setTestWorking] = useState(false);
  const [testError, setTestError] = useState('');
  const alive = useRef(true);

  const load = () => getCallFunnel()
    .then(res => { if (alive.current) { setStatus(res.data); setFailed(false); } })
    .catch(() => { if (alive.current) { setStatus(null); setFailed(true); } });

  useEffect(() => {
    alive.current = true;
    load();
    return () => { alive.current = false; };
  }, []);

  const action = status?.on ? 'off' : 'on';
  const word = CONFIRM_WORD[action];

  const submit = () => {
    setStage('working');
    setError('');
    setCallFunnel(action, typed.trim())
      .then(res => { setStatus(res.data); setStage('idle'); setTyped(''); })
      .catch(e => {
        setError(e?.response?.data?.error || e.message || 'That did not go through');
        setStage('confirm');
        load();
      });
  };

  const card = {
    background: C.card, border: `1px solid ${C.border}`, borderRadius: 12,
    padding: '18px 20px', fontFamily: FONT, maxWidth: 720,
  };
  const btn = (bg, fg) => ({
    background: bg, color: fg, border: 'none', borderRadius: 8,
    padding: '11px 18px', fontSize: 13, fontWeight: 700, fontFamily: FONT,
    cursor: 'pointer', flex: '1 1 160px', minWidth: 0,
  });

  const on = !!status?.on;
  const periods = status?.periods || [];
  const minutes = status?.sessionMinutes === 30 ? 30 : 60;
  const otherMinutes = minutes === 60 ? 30 : 60;

  const changeLength = () => {
    setLenStage('working');
    setLenError('');
    setCallFunnelSessionLength(otherMinutes)
      .then(() => load())
      .then(() => setLenStage('idle'))
      .catch(e => { setLenError(e?.response?.data?.error || e.message || 'That did not go through'); setLenStage('idle'); });
  };

  // Mark period i as a practice run (or clear it). The server wants the exact
  // confirm word, written compare-and-set like the switch flip.
  const submitPeriodTest = (i, test) => {
    setTestWorking(true);
    setTestError('');
    setCallFunnelPeriodTest(i, test, test ? 'MARK TEST' : 'UNMARK TEST')
      .then(res => { if (alive.current) { setStatus(res.data); setTestIdx(null); } })
      .catch(e => { if (alive.current) setTestError(e?.response?.data?.error || e.message || 'That did not go through'); })
      .finally(() => { if (alive.current) setTestWorking(false); });
  };

  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginBottom: 6 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.heading }}>Call funnel switch</div>
        {status && (
          <span style={{
            fontSize: 10, fontWeight: 800, letterSpacing: '0.08em',
            color: on ? C.green : C.accent, background: on ? C.greenBg : C.barTrack,
            border: `1px solid ${on ? '#BFE3D2' : C.border}`,
            borderRadius: 20, padding: '3px 10px', whiteSpace: 'nowrap',
          }}>
            {on ? 'ON' : 'OFF'}
          </span>
        )}
      </div>

      <div style={{ fontSize: 11.5, color: C.accent, lineHeight: 1.6, marginBottom: 14 }}>
        When ON, new people are meant to be sent to the complimentary Personal Blueprint Session instead of
        the course. Switching ON sends new people to the booking pop-up on the masterclass page instead of
        the course, and starts the call-funnel emails. <strong style={{ color: C.heading }}>Do not switch on
        until the practice run.</strong>
      </div>

      {failed && (
        <div style={{ fontSize: 12, color: C.red, background: C.redBg, borderRadius: 8, padding: '10px 12px', marginBottom: 4, lineHeight: 1.5 }}>
          Couldn't read the switch, so its state is not shown and it can't be changed from here.
          Reload to try again.
          <div style={{ marginTop: 8 }}>
            <button onClick={load} style={{ ...btn(C.barTrack, C.heading), flex: 'none', padding: '7px 14px', fontSize: 12 }}>Try again</button>
          </div>
        </div>
      )}

      {!status && !failed && (
        <div style={{ fontSize: 12, color: C.accent }}>Loading…</div>
      )}

      {status && (
        <>
          <div style={{
            background: on ? C.greenBg : '#FDFAF8', border: `1px solid ${on ? '#BFE3D2' : C.border}`,
            borderRadius: 8, padding: '10px 12px', marginBottom: 14,
            fontSize: 13, fontWeight: 700, color: C.heading, lineHeight: 1.5,
          }}>
            {on ? <>ON since {fmtLocal(status.since)}</> : 'OFF'}
          </div>

          <div style={{ fontSize: 11, fontWeight: 700, color: C.heading, marginBottom: 6 }}>History</div>
          {periods.length === 0 ? (
            <div style={{ fontSize: 11.5, color: C.accent, marginBottom: 14 }}>Never switched on.</div>
          ) : (
            <div style={{ marginBottom: 14 }}>
              {periods.map((p, i) => (
                <div key={p.from} style={{ padding: '6px 0', borderTop: i ? `1px solid ${C.border}` : 'none' }}>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', fontSize: 11.5, color: C.text }}>
                    <span style={{ fontWeight: 700, color: C.heading, minWidth: 18 }}>{i + 1}.</span>
                    <span>ON {fmtLocal(p.from)}</span>
                    <span style={{ color: C.accent }}>→</span>
                    <span>{p.to ? `OFF ${fmtLocal(p.to)}` : <strong style={{ color: C.green }}>still on</strong>}</span>
                    {p.test && (
                      <span style={{
                        fontSize: 9.5, fontWeight: 800, letterSpacing: '0.07em', color: '#92650A',
                        background: C.amberBg, border: '1px solid #F5D69A', borderRadius: 20, padding: '2px 8px',
                      }}>
                        TEST — NOT COUNTED
                      </span>
                    )}
                    {isOwner && testIdx !== i && (
                      <button
                        onClick={() => { setTestIdx(i); setTestError(''); }}
                        style={{ ...btn(C.barTrack, C.heading), flex: 'none', padding: '4px 10px', fontSize: 11 }}
                      >
                        {p.test ? 'Unmark test' : 'Mark as test'}
                      </button>
                    )}
                  </div>
                  {isOwner && testIdx === i && (
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 6 }}>
                      <span style={{ fontSize: 11.5, color: '#92650A' }}>
                        {p.test
                          ? 'This period will count as real traffic again in the call-funnel numbers. Sure?'
                          : 'This period (landings, opt-ins, ad spend and every stage) will be left out of the call-funnel numbers. Nothing is deleted, and the switch is not changed. Sure?'}
                      </span>
                      <button
                        onClick={() => submitPeriodTest(i, !p.test)}
                        disabled={testWorking}
                        style={{ ...btn(C.heading, '#fff'), flex: 'none', padding: '5px 12px', fontSize: 11 }}
                      >
                        {testWorking ? 'Working…' : (p.test ? 'Yes — unmark' : 'Yes — mark as test')}
                      </button>
                      <button
                        onClick={() => { setTestIdx(null); setTestError(''); }}
                        disabled={testWorking}
                        style={{ ...btn(C.barTrack, C.heading), flex: 'none', padding: '5px 12px', fontSize: 11 }}
                      >
                        Cancel
                      </button>
                      {testError && <span style={{ flex: '1 1 100%', fontSize: 11.5, color: C.red }}>{testError}</span>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* ── Session length ─────────────────────────────────────────────── */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '10px 12px', background: '#FDFAF8', border: `1px solid ${C.border}`, borderRadius: 8, marginBottom: 14 }}>
            <div style={{ flex: '1 1 160px', fontSize: 12.5, color: C.heading }}>
              <strong>Session length: {minutes} minutes</strong>
              <div style={{ fontSize: 11, color: C.accent, marginTop: 2 }}>Which Personal Blueprint Session the pop-up books.</div>
            </div>
            {isOwner && lenStage === 'idle' && (
              <button onClick={() => { setLenStage('confirm'); setLenError(''); }} style={{ ...btn(C.barTrack, C.heading), flex: 'none', padding: '8px 14px', fontSize: 12 }}>
                Switch to {otherMinutes} minutes
              </button>
            )}
            {isOwner && lenStage !== 'idle' && (
              <div style={{ flex: '1 1 100%', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <span style={{ fontSize: 12, color: '#92650A' }}>From the next booking on, the pop-up will offer {otherMinutes}-minute sessions. Sure?</span>
                <button onClick={changeLength} disabled={lenStage === 'working'} style={{ ...btn(C.heading, '#fff'), flex: 'none', padding: '8px 14px', fontSize: 12 }}>
                  {lenStage === 'working' ? 'Working…' : `Yes — ${otherMinutes} minutes`}
                </button>
                <button onClick={() => setLenStage('idle')} disabled={lenStage === 'working'} style={{ ...btn(C.barTrack, C.heading), flex: 'none', padding: '8px 14px', fontSize: 12 }}>
                  Cancel
                </button>
              </div>
            )}
            {lenError && <div style={{ flex: '1 1 100%', fontSize: 12, color: C.red }}>{lenError}</div>}
          </div>

          {error && <div style={{ fontSize: 12, color: C.red, marginBottom: 10 }}>{error}</div>}

          {isOwner && (stage === 'confirm' || stage === 'working') && (
            <div style={{ background: C.amberBg, border: '1px solid #F5D69A', borderRadius: 8, padding: '12px 14px' }}>
              <div style={{ fontSize: 12.5, color: '#92650A', lineHeight: 1.6, marginBottom: 10 }}>
                {action === 'on' ? (
                  <>Once the call funnel is built, switching ON means: <strong>new people will be sent to
                  the Personal Blueprint Session instead of the course</strong>, and course-selling emails stop
                  for people who arrive from now on. Anyone already in the course or partway through keeps
                  everything.</>
                ) : (
                  <>Once the call funnel is built, switching OFF means: <strong>new people will be sent to the
                  course again</strong>. People who arrived while it was ON stay in the call funnel.</>
                )}
              </div>
              <label style={{ display: 'block', fontSize: 11.5, color: C.heading, marginBottom: 6 }}>
                Type <strong>{word}</strong> to confirm
              </label>
              <input
                value={typed}
                onChange={e => setTyped(e.target.value)}
                autoFocus
                disabled={stage === 'working'}
                style={{
                  width: '100%', boxSizing: 'border-box', padding: '9px 10px', fontSize: 14,
                  border: `1px solid ${C.border}`, borderRadius: 8, fontFamily: FONT, marginBottom: 10,
                }}
              />
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button
                  onClick={submit}
                  disabled={typed.trim() !== word || stage === 'working'}
                  style={{
                    ...btn(typed.trim() === word ? (action === 'on' ? C.green : C.heading) : C.barTrack,
                           typed.trim() === word ? '#fff' : C.accent),
                    cursor: typed.trim() === word && stage !== 'working' ? 'pointer' : 'not-allowed',
                  }}
                >
                  {stage === 'working' ? 'Working…' : (action === 'on' ? 'Switch ON' : 'Switch OFF')}
                </button>
                <button
                  onClick={() => { setStage('idle'); setTyped(''); setError(''); }}
                  disabled={stage === 'working'}
                  style={btn(C.barTrack, C.heading)}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {isOwner && stage === 'idle' && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button onClick={() => { setStage('confirm'); setError(''); }} style={btn(on ? C.heading : C.cta, '#fff')}>
                {on ? 'Switch OFF' : 'Switch ON'}
              </button>
            </div>
          )}
        </>
      )}

      <div style={{ fontSize: 10, color: C.accent, marginTop: 12, lineHeight: 1.5 }}>
        Times are your device's local time.
      </div>
    </div>
  );
};

// ── THE FUNNEL NUMBERS (GET /api/admin/call-funnel-stats) ───────────────────
// Counted by the server (backend services/callFunnelStats.js): only people who
// opted in while the switch was ON, distinct people at every stage, the same
// exclusions as Ad Metrics. A range is clipped to the ON periods on the server.
// A failed load shows an error — never zeros.
//
// THE DATE PICKER is the shared one (adminShared TimeframeControlWithDates —
// the same presets, markers and custom range as Ad Metrics / Pages &
// Masterclass), with its "All time" button shown as "Every ON period" (the
// default: no dates sent, every ON period counted). Like those tabs, the
// choice is not remembered between visits.
//
// THE ROWS, their order, labels and EVERY percentage come from the server
// (callFunnelStats.js stageRows) — this card only prints them.
const RANGE_LABELS = { all: 'Every ON period' };

const money = (n, cur) => (n == null ? '—' : `${cur === 'AUD' ? 'A$' : cur === 'GBP' ? '£' : cur === 'USD' ? 'US$' : `${cur} `}${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
// A percentage the server worked out: "12.5% of opt-ins"; a 0 denominator → "—".
const pctText = (p) => (p == null ? '' : `${p.value == null ? '—' : `${Number(p.value).toFixed(1)}%`} ${p.label}`);
const dayMonth = (iso) => new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });

export const CallFunnelNumbers = () => {
  const { range, setRange, customFrom, setCustomFrom, customTo, setCustomTo, from, to } = useTimeframe('all');
  const [paidOnly, setPaidOnly] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    if (range === 'custom' && (!customFrom || !customTo)) return () => { alive = false; };
    setLoading(true); setError('');
    getCallFunnelStats({ from, to, paidOnly })
      .then(res => { if (alive) { setData(res.data); setLoading(false); } })
      .catch(e => {
        if (!alive) return;
        setData(null); setLoading(false);
        setError(e?.response?.status === 403 ? 'Only the owner can see these numbers.' : (e?.response?.data?.error || 'Could not load the call funnel numbers.'));
      });
    return () => { alive = false; };
  }, [range, from, to, paidOnly]);

  const card = { background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: '18px 20px', fontFamily: FONT, maxWidth: 720, marginTop: 18 };
  const chip = (active) => ({ background: active ? C.heading : '#fff', color: active ? '#fff' : C.heading, border: `1px solid ${active ? C.heading : C.border}`, borderRadius: 999, padding: '5px 11px', fontSize: 12, fontWeight: 600, fontFamily: FONT, cursor: 'pointer' });

  const header = (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
      <div style={{ fontSize: 15, fontWeight: 700, color: C.heading }}>The call funnel</div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <button style={chip(paidOnly)} onClick={() => setPaidOnly(true)}>Paid only</button>
        <button style={chip(!paidOnly)} onClick={() => setPaidOnly(false)}>All traffic</button>
      </div>
    </div>
  );

  if (data && data.everOn === false) {
    return (
      <div style={card}>
        {header}
        <div style={{ fontSize: 13, color: C.accent }}>No numbers yet — counting starts the moment you switch on.</div>
      </div>
    );
  }

  const s = data?.session || {};
  const noShowPct = data?.noShowPct || {};
  const spend = data?.spend;
  const costPer = data?.costPer || {};
  const spendKnown = spend && spend.error == null;
  // The lines under each row (the row itself — label, count, % — is the server's).
  const notes = {
    landings: { extra: spendKnown ? `Ad spend ${money(spend.amount, spend.currency)}` : 'Ad spend could not be read' },
    optedIn: { extra: `Cost per opt-in ${money(costPer.optIn, 'AUD')}` },
    step1: { sub: 'Includes "Finished step 1" from the earlier two-step pop-up' },
    booked: { extra: `Cost per booking ${money(costPer.booking, 'AUD')}`,
      sub: 'A booking that was later cancelled is not counted here (see the next line)' },
    cancelled: { sub: 'Information only — booked, then cancelled, and not booked again' },
    answered: { sub: 'Information only — of those booked, who sent the questions asked after booking' },
    showed: { extra: `Cost per show ${money(costPer.show, 'AUD')}`,
      sub: `No-show ${s.noShow ?? 0} (${pctText({ value: noShowPct.ofOptIns, label: 'of opt-ins' })} · ${pctText({ value: noShowPct.ofBooked, label: 'of booked' })}) · Awaiting outcome ${s.awaitingOutcome ?? 0} · Upcoming ${s.upcoming ?? 0}` +
        (s.rescheduled ? ` · Rescheduled, no new time ${s.rescheduled}` : '') + (s.noAppointmentFound ? ` · Booking not found ${s.noAppointmentFound}` : '') },
    converted: { extra: `Cost per conversion ${money(costPer.conversion, 'AUD')}`,
      sub: `Revenue ${(data?.revenue || []).length ? data.revenue.map(r => money(r.amount, r.currency)).join(' + ') : 'none yet'}` },
  };
  const rows = (data?.stages || []).map(r => ({ ...r, ...notes[r.key] }));

  return (
    <div style={card}>
      {header}
      <TimeframeControlWithDates
        range={range} setRange={setRange}
        customFrom={customFrom} setCustomFrom={setCustomFrom}
        customTo={customTo} setCustomTo={setCustomTo}
        labels={RANGE_LABELS}
      />

      {error ? (
        <div style={{ background: C.redBg, color: C.red, borderRadius: 8, padding: '10px 12px', fontSize: 13 }}>{error}</div>
      ) : loading || !data ? (
        <div style={{ fontSize: 13, color: C.accent }}>Loading…</div>
      ) : (
        <>
          {(data.windows || []).length === 0 ? (
            <div style={{ fontSize: 12.5, color: C.accent, marginBottom: 10 }}>The switch was not on at any time in this range, so nobody is counted.</div>
          ) : (
            <div style={{ fontSize: 11, color: C.accent, marginBottom: 10 }}>
              Counted only while the switch was ON: {data.windows.map(w => `${dayMonth(w.from)} – ${dayMonth(w.to)}`).join(' · ')}
            </div>
          )}
          {rows.map(r => (
            <div key={r.key} style={{ borderTop: `1px solid ${C.border}`, padding: '9px 0', display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ minWidth: 0, flex: '1 1 220px' }}>
                <div style={{ fontSize: 13.5, color: C.text, fontWeight: 600 }}>{r.label}</div>
                {r.sub && <div style={{ fontSize: 11.5, color: C.accent, marginTop: 2 }}>{r.sub}</div>}
                {r.extra && <div style={{ fontSize: 11.5, color: C.accent, marginTop: 2 }}>{r.extra}</div>}
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: C.heading }}>{r.count}</div>
                {r.main && <div style={{ fontSize: 12, color: C.text, fontWeight: 600 }}>{pctText(r.main)}</div>}
                {r.previous && <div style={{ fontSize: 10.5, color: C.accent }}>{pctText(r.previous)}</div>}
              </div>
            </div>
          ))}
          <div style={{ fontSize: 10.5, color: C.accent, marginTop: 10, lineHeight: 1.5 }}>
            People who opted in while the switch was ON, and how far each has got since. {paidOnly ? 'Paid only: opt-ins tagged from an ad; landings from the opt-in campaign, as on Ad Metrics.' : 'All traffic.'} Spend is every campaign on the days the switch was on (Meta reports whole days).
          </div>
        </>
      )}
    </div>
  );
};

export const AdminAdsToCall = () => (
  <div style={{ fontFamily: FONT, color: C.text }}>
    <CallFunnelSwitchCard />
    <CallFunnelNumbers />
  </div>
);
